// App.js CORRIGÉ
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { 
  BrowserRouter as Router,
  Route, 
  Routes, 
  Link, 
  useLocation,
  useNavigate
} from 'react-router-dom';
import { 
  ChevronRight, 
  ChevronDown, 
  ChevronLeft,
  Folder, 
  File, 
  Menu, 
  X, 
  Search,
  Settings, 
  Moon, 
  Sun, 
  Grid, 
  List,
  Maximize2, 
  Minimize2,
  BookOpen,
} from 'lucide-react';
import { motion } from 'framer-motion';

// 🔧 SOLUTION 1: Styles globaux unifiés
const GlobalStyles = () => (
  <style>{`
    /* Variables CSS globales pour cohérence */
    :root {
      --app-bg: #f5f5f5;
      --app-text: #000000;
      --sidebar-bg: #ffffff;
      --sidebar-text: #374151;
      --border-color: rgba(0, 0, 0, 0.1);
      --hover-bg: rgba(0, 0, 0, 0.05);
    }
    
    [data-theme="dark"] {
      --app-bg: #0a0a0a;
      --app-text: #ffffff;
      --sidebar-bg: #1f2937;
      --sidebar-text: #e5e7eb;
      --border-color: rgba(255, 255, 255, 0.1);
      --hover-bg: rgba(255, 255, 255, 0.05);
    }
    
    /* Styles de base pour éviter les conflits */
    .app-container {
      background: var(--app-bg);
      color: var(--app-text);
      min-height: 100vh;
      transition: all 0.3s ease;
    }
    
    .app-sidebar {
      background: var(--sidebar-bg);
      color: var(--sidebar-text);
      border-color: var(--border-color);
    }
    
    .app-main-content {
      background: var(--app-bg);
      min-height: 100vh;
    }
    
    /* Permettre aux pages de surcharger les styles si nécessaire */
    .page-content {
      position: relative;
      z-index: 1;
    }
    
    /* Styles spécifiques pour les pages IA */
    .ia-page-container {
      background: #0a0a0a !important;
      color: #ffffff !important;
      min-height: 100vh;
    }
    
    .ia-page-container * {
      color: inherit;
    }
  `}</style>
);

// Composant de carte pour la page d'accueil
const FolderCard = ({ name, path, onClick }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow"
    onClick={onClick}
  >
    <div className="flex items-center space-x-3">
      <Folder className="text-blue-500" size={24} />
      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{name}</h3>
    </div>
  </motion.div>
);

// Composant TreeNode optimisé
const TreeNode = ({ name, content, path = '', depth = 0, searchTerm = '' }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const location = useLocation();
  const isFile = React.isValidElement(content);
  const hasChildren = !isFile && Object.keys(content).length > 0;
  const isActive = location.pathname === `/${path}${name}`.toLowerCase();

  if (searchTerm && !name.toLowerCase().includes(searchTerm.toLowerCase())) {
    return null;
  }

  return (
    <div className="select-none">
      <div 
        className={`flex items-center py-2 px-3 rounded-lg transition-all duration-200 ${
          isActive 
            ? 'bg-blue-500 text-white' 
            : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
        }`}
        style={{ paddingLeft: `${depth * 16 + 12}px` }}
      >
        {hasChildren && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-1 rounded-lg mr-1 ${
              isActive ? 'hover:bg-blue-600' : 'hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {isExpanded ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        )}
        
        {isFile ? (
          <>
            <File size={16} className="mr-2" />
            <Link 
              to={`/${path}${name}`.toLowerCase()} 
              className={`flex-1 ${isActive ? 'text-white' : 'text-gray-700 dark:text-gray-300'}`}
            >
              {name}
            </Link>
          </>
        ) : (
          <>
            <Folder size={16} className="mr-2" />
            <span className="flex-1">{name}</span>
          </>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="ml-2">
          {Object.entries(content).map(([key, value]) => (
            <TreeNode
              key={key}
              name={key}
              content={value}
              path={`${path}${name}/`}
              depth={depth + 1}
              searchTerm={searchTerm}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// 🔧 SOLUTION 2: Wrapper pour pages IA
const IAPageWrapper = ({ children }) => {
  return (
    <div className="ia-page-container">
      <div className="page-content">
        {children}
      </div>
    </div>
  );
};

// 🔧 SOLUTION 3: Détection du type de page
const isIAPage = (pathname) => {
  return pathname.includes('/ia/') || 
         pathname.includes('agent') || 
         pathname.includes('rag') ||
         pathname.includes('architecture') ||
         pathname.includes('api-documentation') ||
         pathname.includes('performance');
};

// Composant principal AppContent
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isOpen, setIsOpen] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDark, setIsDark] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Import des pages
  const pageModules = require.context('./pages', true, /\.js$/);
  const pagesTree = useMemo(() => {
    const tree = {};
    pageModules.keys().forEach((fileName) => {
      const pathParts = fileName.replace('./', '').replace('.js', '').split('/');
      let current = tree;
      pathParts.forEach((part, index) => {
        if (!current[part]) {
          current[part] = index === pathParts.length - 1
            ? React.createElement(pageModules(fileName).default)
            : {};
        }
        current = current[part];
      });
    });
    return tree;
  }, []);

  // 🔧 SOLUTION 4: Gestion du thème améliorée
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  // Gestion du plein écran
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Gestion du redimensionnement
  const handleResizeStart = (e) => {
    setIsResizing(true);
    window.addEventListener('mousemove', handleResize);
    window.addEventListener('mouseup', handleResizeEnd);
  };

  const handleResize = (e) => {
    if (isResizing) {
      const newWidth = e.clientX;
      if (newWidth > 200 && newWidth < window.innerWidth * 0.8) {
        setSidebarWidth(newWidth);
      }
    }
  };

  const handleResizeEnd = () => {
    setIsResizing(false);
    window.removeEventListener('mousemove', handleResize);
    window.removeEventListener('mouseup', handleResizeEnd);
  };

  // 🔧 SOLUTION 5: Détection du type de page courante
  const currentPageIsIA = isIAPage(location.pathname);
  
  return (
    <>
      <GlobalStyles />
      <div className={`app-container h-screen flex ${isDark ? 'dark' : ''}`}>
        {/* Sidebar */}
        <motion.div
          initial={false}
          animate={{
            width: sidebarWidth,
            x: isOpen ? 0 : -sidebarWidth
          }}
          className="app-sidebar fixed top-0 bottom-0 left-0 z-40 shadow-lg flex flex-col"
        >
          {/* Sidebar Header */}
          <div className="h-16 border-b flex items-center justify-between px-4 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <BookOpen size={24} className="text-blue-500" />
              <h1 className="text-xl font-bold">
                Explorer
              </h1>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsDark(!isDark)}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              <button
                onClick={toggleFullscreen}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="p-4 border-b flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search files..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 transition-shadow"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Tree View with Scroll */}
          <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
            <div className="p-4">
              {Object.entries(pagesTree).map(([key, value]) => (
                <TreeNode
                  key={key}
                  name={key}
                  content={value}
                  searchTerm={searchTerm}
                />
              ))}
            </div>
          </div>
        </motion.div>

        {/* Resizer */}
        {isOpen && (
          <div
            className="fixed top-0 bottom-0 w-1 cursor-ew-resize z-50 hover:bg-blue-500 hover:w-1 transition-all"
            style={{ left: sidebarWidth }}
            onMouseDown={handleResizeStart}
          />
        )}

        {/* Main Content Area */}
        <motion.div
          animate={{
            marginLeft: isOpen ? sidebarWidth : 0,
            width: isOpen ? `calc(100% - ${sidebarWidth}px)` : '100%'
          }}
          className="app-main-content flex-1"
        >
          {/* Top Bar - Masqué pour les pages IA */}
          {!currentPageIsIA && (
            <div className="h-16 border-b bg-white dark:bg-gray-800 flex items-center justify-between px-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {isOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
                </button>
              </div>
            </div>
          )}

          {/* Content */}
          <div className={currentPageIsIA ? '' : 'p-6 overflow-auto'}>
            <Routes>
              {/* Home Route avec affichage des cartes */}
              <Route 
                path="/" 
                element={
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {Object.entries(pagesTree).map(([key, value]) => (
                      !React.isValidElement(value) && (
                        <FolderCard
                          key={key}
                          name={key}
                          path={`/${key}`.toLowerCase()}
                          onClick={() => navigate(`/${key}`.toLowerCase())}
                        />
                      )
                    ))}
                  </div>
                }
              />
              
              {/* Routes pour les pages */}
              {pageModules.keys().map((fileName) => {
                const path = `/${fileName.replace('./', '').replace('.js', '').toLowerCase()}`;
                const Component = pageModules(fileName).default;
                
                return (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {/* 🔧 SOLUTION 6: Wrapper conditionnel pour pages IA */}
                        {isIAPage(path) ? (
                          <IAPageWrapper>
                            <Component />
                          </IAPageWrapper>
                        ) : (
                          <Component />
                        )}
                      </motion.div>
                    }
                  />
                );
              })}

              {/* Route 404 */}
              <Route 
                path="*" 
                element={
                  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                    <h2 className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                      Page Not Found
                    </h2>
                    <p className="mt-2 text-gray-500">
                      The page you're looking for doesn't exist or has been moved.
                    </p>
                  </div>
                } 
              />
            </Routes>
          </div>
        </motion.div>

        {/* Loading Indicator */}
        {isResizing && (
          <div className="fixed inset-0 pointer-events-none bg-blue-500/10 z-50" />
        )}
      </div>
    </>
  );
};

// Main App Component
const App = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App;